import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, X } from 'lucide-react';

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR', symbol: '\u20AC' },
  { value: 'GBP', label: 'GBP', symbol: '\u00A3' },
  { value: 'NGN', label: 'NGN', symbol: '\u20A6' },
  { value: 'INR', label: 'INR', symbol: '\u20B9' },
  { value: 'ZAR', label: 'ZAR (R)', symbol: 'R' },
];

const TIER_OPTIONS = [
  { value: 'premium_portfolio', label: 'Portfolio Clarity' },
  { value: 'premium_continuity', label: 'Continuity System' },
];

export const PricingCreateForm = ({ createForm, setCreateForm, onSubmit, onClose }) => (
  <Card id="create-pricing-form" className="border-green-500/30 bg-gray-700 shadow-lg">
    <CardHeader>
      <CardTitle className="text-lg text-white">Create New Pricing Row</CardTitle>
      <CardDescription className="text-gray-400">
        One row = one region &times; one tier. Annual is calculated as monthly &times; 10 (2 months free).
      </CardDescription>
    </CardHeader>
    <CardContent>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="region_name" className="text-gray-300">Region name <span className="text-red-400">*</span></Label>
            <Input id="region_name" data-testid="pricing-create-region" type="text" placeholder="e.g., High Income Markets"
              value={createForm.region_name}
              onChange={(e) => setCreateForm({ ...createForm, region_name: e.target.value })}
              className="bg-gray-800 text-white border-gray-600" required />
          </div>
          <div>
            <Label htmlFor="tier" className="text-gray-300">Tier <span className="text-red-400">*</span></Label>
            <select id="tier" data-testid="pricing-create-tier"
              className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-800 text-white"
              value={createForm.tier}
              onChange={(e) => setCreateForm({ ...createForm, tier: e.target.value })}>
              {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="currency" className="text-gray-300">Currency <span className="text-red-400">*</span></Label>
            <select id="currency" data-testid="pricing-create-currency"
              className="w-full px-3 py-2 border border-gray-600 rounded-md bg-gray-800 text-white"
              value={createForm.currency}
              onChange={(e) => {
                const opt = CURRENCY_OPTIONS.find(o => o.value === e.target.value);
                setCreateForm({ ...createForm, currency: e.target.value, symbol: opt?.symbol || '$' });
              }}>
              {CURRENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="symbol" className="text-gray-300">Symbol</Label>
            <Input id="symbol" type="text" value={createForm.symbol}
              onChange={(e) => setCreateForm({ ...createForm, symbol: e.target.value })}
              className="bg-gray-800 text-white border-gray-600" maxLength={3} />
          </div>
        </div>

        <div>
          <Label htmlFor="monthly_amount" className="text-gray-300">Monthly amount <span className="text-red-400">*</span></Label>
          <Input id="monthly_amount" data-testid="pricing-create-monthly" type="number" step="0.01" placeholder="e.g., 22.50"
            value={createForm.monthly_amount}
            onChange={(e) => setCreateForm({ ...createForm, monthly_amount: e.target.value })}
            className="bg-gray-800 text-white border-gray-600" required />
          {createForm.monthly_amount && parseFloat(createForm.monthly_amount) > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Annual: <span className="text-purple-400 font-medium">{createForm.symbol}{(parseFloat(createForm.monthly_amount) * 10).toFixed(2)}</span> (2 months free).
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="countries" className="text-gray-300">Countries <span className="text-red-400">*</span></Label>
          <Input id="countries" data-testid="pricing-create-countries" type="text"
            placeholder="USA, Canada, Mexico (comma-separated)"
            value={createForm.countries}
            onChange={(e) => setCreateForm({ ...createForm, countries: e.target.value })}
            className="bg-gray-800 text-white border-gray-600" required />
          <p className="text-xs text-gray-500 mt-1">Comma-separated. Same list is used for the sibling tier row in this region.</p>
        </div>

        <div className="flex items-center space-x-2">
          <input type="checkbox" id="is_active_create" checked={createForm.is_active}
            onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
            className="w-4 h-4 rounded border-gray-600" />
          <Label htmlFor="is_active_create" className="cursor-pointer text-gray-300">Active (visible to users immediately)</Label>
        </div>

        <div className="flex space-x-2 pt-2">
          <Button type="submit" data-testid="pricing-create-submit" className="bg-green-500 hover:bg-green-600 text-white"><Save className="w-4 h-4 mr-2" />Create Pricing Row</Button>
          <Button type="button" variant="outline" onClick={onClose} className="border-gray-600 text-gray-300 hover:bg-gray-700"><X className="w-4 h-4 mr-2" />Cancel</Button>
        </div>
      </form>
    </CardContent>
  </Card>
);
